import { useEffect, useRef, useState } from "react";
import {
    adminListSupportTickets,
    adminGetSupportTicket,
    adminUpdateSupportTicket,
    adminAddSupportTicketNote,
    adminListCannedResponses,
    adminOpenRefund,
} from "@/lib/api";
import { toast } from "sonner";
import {
    AlertTriangle,
    CheckCircle2,
    Circle,
    Clock,
    Search,
    X,
    User,
    FileText,
    CreditCard,
    LifeBuoy,
} from "lucide-react";

const STATUS_LABEL = {
    new: { text: "New", cls: "bg-amber-50 text-amber-900 border-amber-200" },
    reviewing: {
        text: "Reviewing",
        cls: "bg-sky-50 text-sky-900 border-sky-200",
    },
    waiting_on_user: {
        text: "Waiting on user",
        cls: "bg-violet-50 text-violet-900 border-violet-200",
    },
    resolved: {
        text: "Resolved",
        cls: "bg-emerald-50 text-emerald-900 border-emerald-200",
    },
    closed: { text: "Closed", cls: "bg-bl-bg text-bl-muted border-bl" },
};

const PRIORITY_LABEL = {
    urgent: { text: "Urgent", cls: "bg-rose-50 text-rose-900 border-rose-200" },
    medium: {
        text: "Medium",
        cls: "bg-amber-50 text-amber-900 border-amber-200",
    },
    low: { text: "Low", cls: "bg-bl-bg text-bl-muted border-bl" },
};

const CATEGORY_LABEL = {
    upload: "Upload",
    payment_billing: "Payment / Billing",
    report_access: "Report access",
    report_accuracy: "Report accuracy",
    repair_exposure: "Repair exposure",
    account_login: "Account / Login",
    refund: "Refund",
    quote_request: "Quote request",
    privacy_deletion: "Privacy / Deletion",
    subscription: "Subscription",
    other: "Other",
};

function Badge({ map, value }) {
    const meta = map[value] || { text: value, cls: "bg-bl-bg text-bl-muted border-bl" };
    return (
        <span
            className={"text-xs px-2 py-0.5 rounded-full border " + meta.cls}
            data-testid={`support-badge-${value}`}
        >
            {meta.text}
        </span>
    );
}

function TicketDrawer({ ticketId, onClose, onMutated }) {
    const [loading, setLoading] = useState(true);
    const [ticket, setTicket] = useState(null);
    const [notes, setNotes] = useState([]);
    const [noteDraft, setNoteDraft] = useState("");
    const [adding, setAdding] = useState(false);
    const [canned, setCanned] = useState([]);
    const [refundOpen, setRefundOpen] = useState(false);
    const [refundAmount, setRefundAmount] = useState("");
    const [refundReason, setRefundReason] = useState("");
    const [openingRefund, setOpeningRefund] = useState(false);

    useEffect(() => {
        if (!ticketId) return;
        setLoading(true);
        adminGetSupportTicket(ticketId)
            .then((r) => {
                setTicket(r.ticket);
                setNotes(r.notes || []);
            })
            .catch(() => toast.error("Failed to load ticket."))
            .finally(() => setLoading(false));
        // Background-load canned responses once per drawer open.
        adminListCannedResponses()
            .then((rows) => setCanned(rows || []))
            .catch(() => setCanned([]));
    }, [ticketId]);

    const insertCanned = (id) => {
        const c = canned.find((x) => x.canned_id === id);
        if (!c) return;
        setNoteDraft((prev) => (prev ? `${prev}\n\n${c.body}` : c.body));
    };

    const openRefund = async () => {
        setOpeningRefund(true);
        try {
            const r = await adminOpenRefund(ticketId, {
                amount_usd: refundAmount ? parseFloat(refundAmount) : null,
                reason: refundReason || null,
            });
            if (r.existing) {
                toast.info("A pending refund already exists for this ticket.");
            } else {
                toast.success("Refund request opened.");
            }
            setRefundOpen(false);
            setRefundAmount("");
            setRefundReason("");
            onMutated?.();
            // Refetch ticket so the new urgent/reviewing status shows.
            const fresh = await adminGetSupportTicket(ticketId);
            setTicket(fresh.ticket);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not open refund.");
        } finally {
            setOpeningRefund(false);
        }
    };

    const updateStatus = async (status) => {
        try {
            const fresh = await adminUpdateSupportTicket(ticketId, { status });
            setTicket(fresh);
            toast.success("Status updated.");
            onMutated?.();
        } catch {
            toast.error("Update failed.");
        }
    };

    const updatePriority = async (priority) => {
        try {
            const fresh = await adminUpdateSupportTicket(ticketId, {
                priority,
            });
            setTicket(fresh);
            onMutated?.();
        } catch {
            toast.error("Update failed.");
        }
    };

    const addNote = async () => {
        if (!noteDraft.trim()) return;
        setAdding(true);
        try {
            const n = await adminAddSupportTicketNote(ticketId, noteDraft.trim());
            setNotes((prev) => [...prev, n]);
            setNoteDraft("");
            toast.success("Note added.");
        } catch {
            toast.error("Note failed.");
        } finally {
            setAdding(false);
        }
    };

    if (!ticketId) return null;

    return (
        <div
            className="fixed inset-0 z-50 bg-bl-ink/40"
            onClick={onClose}
            data-testid="support-ticket-drawer"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="absolute right-0 top-0 h-full w-full md:w-[640px] bg-bl-surface border-l border-bl shadow-2xl overflow-y-auto"
            >
                <div className="sticky top-0 bg-bl-surface border-b border-bl px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="bl-eyebrow">Support ticket</div>
                        <div className="font-mono-bl text-xs text-bl-muted">
                            {ticket?.ticket_id || ticketId}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-bl-muted hover:text-bl"
                        data-testid="support-drawer-close-btn"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {loading || !ticket ? (
                    <div className="p-6 text-bl-muted">Loading…</div>
                ) : (
                    <div className="p-6 space-y-5">
                        <div>
                            <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                Subject
                            </div>
                            <h3 className="mt-1 font-display font-semibold text-xl text-bl">
                                {ticket.subject}
                            </h3>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                <Badge map={PRIORITY_LABEL} value={ticket.priority} />
                                <Badge map={STATUS_LABEL} value={ticket.status} />
                                <span className="text-xs text-bl-muted">
                                    {CATEGORY_LABEL[ticket.category] ||
                                        ticket.category}
                                </span>
                                <span className="text-xs text-bl-muted">
                                    ·{" "}
                                    {new Date(
                                        ticket.created_at,
                                    ).toLocaleString()}
                                </span>
                            </div>
                        </div>

                        <div className="bl-card p-4">
                            <div className="text-xs text-bl-muted flex items-center gap-2">
                                <User className="w-3.5 h-3.5" />
                                <span>{ticket.name || "(no name)"}</span>
                                <span>·</span>
                                <span className="font-mono-bl">
                                    {ticket.email}
                                </span>
                            </div>
                            <p className="mt-3 text-sm text-bl whitespace-pre-wrap">
                                {ticket.description}
                            </p>
                        </div>

                        {/* Status + priority quick-set */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted mb-1.5">
                                    Status
                                </div>
                                <select
                                    value={ticket.status}
                                    onChange={(e) =>
                                        updateStatus(e.target.value)
                                    }
                                    className="w-full px-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm"
                                    data-testid="support-status-select"
                                >
                                    {Object.keys(STATUS_LABEL).map((s) => (
                                        <option key={s} value={s}>
                                            {STATUS_LABEL[s].text}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted mb-1.5">
                                    Priority
                                </div>
                                <select
                                    value={ticket.priority}
                                    onChange={(e) =>
                                        updatePriority(e.target.value)
                                    }
                                    className="w-full px-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm"
                                    data-testid="support-priority-select"
                                >
                                    {Object.keys(PRIORITY_LABEL).map((p) => (
                                        <option key={p} value={p}>
                                            {PRIORITY_LABEL[p].text}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Diagnostic snapshot — admin-only */}
                        <div className="bl-card p-4">
                            <div className="bl-eyebrow flex items-center gap-2">
                                <FileText className="w-3 h-3" />
                                <span>Diagnostic snapshot</span>
                            </div>
                            <pre
                                className="mt-3 font-mono-bl text-[11px] text-bl-muted bg-bl-bg p-3 rounded overflow-x-auto leading-relaxed"
                                data-testid="support-diagnostic-snapshot"
                            >
                                {JSON.stringify(
                                    ticket.diagnostic_snapshot || {},
                                    null,
                                    2,
                                )}
                            </pre>
                        </div>

                        {/* Internal notes */}
                        <div>
                            <div className="bl-eyebrow mb-2">
                                Internal notes (admin-only)
                            </div>
                            <div className="space-y-2">
                                {notes.length === 0 && (
                                    <p className="text-xs text-bl-muted">
                                        No notes yet.
                                    </p>
                                )}
                                {notes.map((n) => (
                                    <div
                                        key={n.note_id}
                                        className="bl-card p-3 text-sm"
                                        data-testid="support-note-row"
                                    >
                                        <div className="text-[11px] text-bl-muted font-mono-bl mb-1">
                                            {new Date(
                                                n.created_at,
                                            ).toLocaleString()}{" "}
                                            · {n.admin_id}
                                        </div>
                                        <div className="text-bl whitespace-pre-wrap">
                                            {n.note}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-3 flex gap-2">
                                <textarea
                                    rows={2}
                                    value={noteDraft}
                                    onChange={(e) =>
                                        setNoteDraft(e.target.value)
                                    }
                                    placeholder="Add an internal note…"
                                    className="flex-1 px-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary resize-none"
                                    data-testid="support-note-input"
                                />
                                <button
                                    onClick={addNote}
                                    disabled={
                                        adding || !noteDraft.trim()
                                    }
                                    className="bl-btn-primary text-sm"
                                    data-testid="support-add-note-btn"
                                >
                                    Add
                                </button>
                            </div>
                            {canned.length > 0 && (
                                <div className="mt-2 flex items-center gap-2 text-xs flex-wrap">
                                    <span className="text-bl-muted">Insert canned:</span>
                                    <select
                                        defaultValue=""
                                        onChange={(e) => {
                                            if (e.target.value) {
                                                insertCanned(e.target.value);
                                                e.target.value = "";
                                            }
                                        }}
                                        className="px-2 py-1 rounded border border-bl bg-bl-surface text-bl text-xs"
                                        data-testid="support-canned-select"
                                    >
                                        <option value="">Choose a template…</option>
                                        {canned.map((c) => (
                                            <option key={c.canned_id} value={c.canned_id}>
                                                {c.category ? `${c.category} · ${c.name}` : c.name}
                                            </option>
                                        ))}
                                    </select>
                                    <span className="text-bl-muted">
                                        Manage in Support Settings.
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Quick links */}
                        {(ticket.related_report_id ||
                            ticket.related_upload_id) && (
                            <div className="bl-card p-4">
                                <div className="bl-eyebrow mb-2">
                                    Quick links
                                </div>
                                <div className="flex flex-wrap gap-2 text-xs">
                                    {ticket.related_report_id && (
                                        <a
                                            href={`/report/${ticket.related_report_id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-3 py-1.5 rounded border border-bl text-bl hover:border-bl-primary hover:text-bl-primary"
                                        >
                                            View report
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Refund workflow (only shown for relevant categories) */}
                        {(ticket.category === "refund" ||
                            ticket.category === "payment_billing" ||
                            ticket.related_payment_id) && (
                            <div className="bl-card p-4" data-testid="support-refund-panel">
                                <div className="bl-eyebrow mb-2">Refund workflow</div>
                                {refundOpen ? (
                                    <div className="space-y-2">
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            placeholder="Refund amount (USD, optional)"
                                            value={refundAmount}
                                            onChange={(e) => setRefundAmount(e.target.value)}
                                            className="w-full px-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                                            data-testid="support-refund-amount"
                                        />
                                        <textarea
                                            rows={2}
                                            placeholder="Reason (internal)"
                                            value={refundReason}
                                            onChange={(e) => setRefundReason(e.target.value)}
                                            className="w-full px-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary resize-y"
                                            data-testid="support-refund-reason"
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                onClick={openRefund}
                                                disabled={openingRefund}
                                                className="bl-btn-primary text-xs"
                                                data-testid="support-refund-submit"
                                            >
                                                {openingRefund ? "Opening…" : "Open refund request"}
                                            </button>
                                            <button
                                                onClick={() => setRefundOpen(false)}
                                                className="bl-btn-secondary text-xs"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                        <p className="text-[10px] text-bl-muted leading-relaxed">
                                            This records the refund internally and bumps the
                                            ticket to urgent. Stripe refunds are processed
                                            out-of-band (manual action in dashboard).
                                        </p>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => setRefundOpen(true)}
                                        className="bl-btn-secondary text-xs"
                                        data-testid="support-refund-open"
                                    >
                                        <CreditCard className="w-3.5 h-3.5" />
                                        Open refund request
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function SupportInbox() {
    const [data, setData] = useState({
        tickets: [],
        status_counts: {},
        priority_counts: {},
    });
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("");
    const [priority, setPriority] = useState("");
    const [q, setQ] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const searchDebounce = useRef(null);

    const load = async () => {
        try {
            setLoading(true);
            const r = await adminListSupportTickets({
                status: status || undefined,
                priority: priority || undefined,
                q: q || undefined,
            });
            setData(r);
        } catch {
            toast.error("Failed to load tickets.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, priority]);

    useEffect(() => {
        if (searchDebounce.current) clearTimeout(searchDebounce.current);
        searchDebounce.current = setTimeout(() => load(), 300);
        return () => {
            if (searchDebounce.current) clearTimeout(searchDebounce.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    return (
        <div className="space-y-4" data-testid="admin-support-inbox">
            <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        onClick={() => setStatus("")}
                        className={
                            "bl-chip " + (status === "" ? "bl-chip-active" : "")
                        }
                        data-testid="support-filter-status-all"
                    >
                        All
                    </button>
                    {Object.keys(STATUS_LABEL).map((s) => (
                        <button
                            key={s}
                            onClick={() => setStatus(s)}
                            className={
                                "bl-chip " +
                                (status === s ? "bl-chip-active" : "")
                            }
                            data-testid={`support-filter-status-${s}`}
                        >
                            {STATUS_LABEL[s].text} ({data.status_counts[s] || 0})
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={priority}
                        onChange={(e) => setPriority(e.target.value)}
                        className="px-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm"
                        data-testid="support-filter-priority"
                    >
                        <option value="">All priorities</option>
                        {Object.keys(PRIORITY_LABEL).map((p) => (
                            <option key={p} value={p}>
                                {`${PRIORITY_LABEL[p].text} (${data.priority_counts[p] || 0})`}
                            </option>
                        ))}
                    </select>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bl-muted" />
                        <input
                            type="search"
                            placeholder="Search…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="pl-9 pr-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary w-56"
                            data-testid="support-search-input"
                        />
                    </div>
                </div>
            </div>

            <div className="bl-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left">
                            {[
                                "Status",
                                "Priority",
                                "Subject",
                                "Category",
                                "User",
                                "Created",
                            ].map((h) => (
                                <th
                                    key={h}
                                    className="px-5 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl"
                                >
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td
                                    colSpan={6}
                                    className="px-5 py-10 text-center text-bl-muted"
                                >
                                    Loading…
                                </td>
                            </tr>
                        )}
                        {!loading && data.tickets.length === 0 && (
                            <tr>
                                <td
                                    colSpan={6}
                                    className="px-5 py-10 text-center text-bl-muted"
                                >
                                    No tickets match these filters.
                                </td>
                            </tr>
                        )}
                        {data.tickets.map((t) => (
                            <tr
                                key={t.ticket_id}
                                className="border-b border-bl last:border-0 hover:bg-bl-bg/30 cursor-pointer"
                                onClick={() => setSelectedId(t.ticket_id)}
                                data-testid={`support-ticket-row-${t.ticket_id}`}
                            >
                                <td className="px-5 py-3">
                                    <Badge map={STATUS_LABEL} value={t.status} />
                                </td>
                                <td className="px-5 py-3">
                                    <Badge
                                        map={PRIORITY_LABEL}
                                        value={t.priority}
                                    />
                                </td>
                                <td className="px-5 py-3 text-bl max-w-md truncate">
                                    {t.subject}
                                </td>
                                <td className="px-5 py-3 text-bl-muted text-xs">
                                    {CATEGORY_LABEL[t.category] || t.category}
                                </td>
                                <td className="px-5 py-3 text-bl-muted text-xs font-mono-bl">
                                    {t.email}
                                </td>
                                <td className="px-5 py-3 text-bl-muted text-xs font-mono-bl">
                                    {new Date(
                                        t.created_at,
                                    ).toLocaleDateString()}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <TicketDrawer
                ticketId={selectedId}
                onClose={() => setSelectedId(null)}
                onMutated={load}
            />
        </div>
    );
}
